import { useState, useEffect, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Button,
  Card,
  Stack,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { AuthenticationContext } from "@cloudmlm/cloudmlm-auth";
import urlConstant from "../constants/urlConstants";
import dataConstant from "../constants/dataConstants";
import { httpGet } from "../services/commonService";
import SnackState from "../shared-components/snackbar/snackState";
import CommonSnackBar from "../shared-components/snackbar/commonSnackbar";
import { selectUserDetail } from "../redux/slice/userSlice";

const BecomeAnAffiliate = () => {
  const { accessToken, impersonateEmail } = useContext(AuthenticationContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [snack, closeSnack, showSnackbar] = SnackState();
  const userDetail = useSelector(selectUserDetail);

  const methods = useForm({
    mode: "all",
    defaultValues: {
      agreeToTerms: false,
    },
  });

  const { setValue, handleSubmit, control, watch } = methods;
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isAffiliateDisabled, setIsAffiliateDisabled] = useState(false);
  const [mediaSrc, setMediaSrc] = useState("");
  const [isAlreadyAffiliate, setIsAlreadyAffiliate] = useState(false);

  const rankTypeList = dataConstant.RankType;
  const affiliateRankDetail = rankTypeList.find(
    (item) => item.RankName === dataConstant.RoleType.Affiliate
  );

  useEffect(() => {
    if (userDetail?.UserAccountDetails?.AcctID) {
      const userRoleDetail = userDetail.UserAccountDetails.UserRolesdetail;
      const roleNames = userRoleDetail
        ? userRoleDetail.map((role) => role.RoleName)
        : [];
      const isAffiliate = roleNames.includes(dataConstant.RoleType.Affiliate);
      const isAmbassador = roleNames.includes(dataConstant.RoleType.Ambassador);
      const userIsAlreadyAffiliate =
        (isAffiliate && isAmbassador) ||
        (userDetail.UserAccountDetails.DisplayRankID === affiliateRankDetail?.DisplayRankID &&
          userDetail.UserAccountDetails.RankName === affiliateRankDetail?.RankName &&
          isAffiliate);
      setIsAlreadyAffiliate(userIsAlreadyAffiliate);
      setIsAffiliateDisabled(userIsAlreadyAffiliate);
    }
  }, [userDetail]);

  const getSystemMediaConfigurationDetails = (configurationType) => {
    const url = `${urlConstant.SystemMediaConfiguration.GetSystemMediaConfigurationDetails}?ConfigurationType=${configurationType}`;
    httpGet(url, true, true, accessToken, impersonateEmail)
      .then((response) => {
        if (response.Value?.Status === dataConstant.APIResponseStatus.Success) {
          const responseData = response.Value.Data;
          if (responseData?.EmbededVideoLink) {
            setMediaSrc(responseData.EmbededVideoLink);
          }
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleAffiliatePopupModal = () => {
    getSystemMediaConfigurationDetails(
      dataConstant.MediaConfigurationType.AffiliateVideo
    );
    setIsPopupOpen(true);
    setValue("agreeToTerms", false);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setMediaSrc("");
  };

  const onSubmit = (data) => {
    if (data.agreeToTerms) {
      updateUserAccount(dataConstant.RoleType.Affiliate);
    }
  };

  const updateUserAccount = (roleName) => {
    const url = `${urlConstant.UpGradeAccount.UpgradeUserAccount}?RoleName=${roleName}`;
    httpGet(url, true, true, accessToken, impersonateEmail)
      .then((response) => {
        setIsPopupOpen(false);
        if (response?.Status === dataConstant.APIResponseStatus.Success) {
          showSnackbar(response.description, "success");
          setTimeout(() => {
            navigate("/myWebsiteName");
            window.location.reload();
          }, 1500);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const termsAgreeCheckbox = () => (
    <Controller
      name="agreeToTerms"
      control={control}
      render={({ field }) => (
        <FormControlLabel
          control={
            <Checkbox
              {...field}
              checked={field.value}
              onChange={(e) => field.onChange(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Box
              component="span"
              sx={{ display: "flex", alignItems: "center" }}
            >
              {t("BecomeAnAffiliate.AgreeToTerms")}
              <Link
                to={`/termsandconditions/${dataConstant.termsAndConditionPreviousPage.becomeAnAffiliate}`}
                target="_blank"
                style={{
                  marginLeft: "4px",
                  color: "var(--primary)",
                  textDecoration: "none",
                  fontWeight: 600,
                }}
              >
                {t("BecomeAnAffiliate.TermsConditions")}
              </Link>
            </Box>
          }
        />
      )}
    />
  );

  return (
    <>
      <Box flexDirection="column" gap={2} display="flex">
        <Typography color="secondary.light" variant="h4" fontWeight={600}>
          {t("BecomeAnAffiliate.UpgradeYourAccount")}
        </Typography>
        <Box>
          <Card
            sx={(theme) => ({
              p: {
                xs: "20px",
                sm: "25px 30px",
              },
              background: theme.palette.background.cardGradient,
              border: "1px solid var(--headerbg)",
              borderRadius: "20px",
              height: "100%",
            })}
          >
            <Stack>
              <Stack
                direction="column"
                spacing={2}
                alignItems="center"
                sx={{ width: "100%" }}
              >
                <Box>
                  <p className="line-height-2">
                    {t("BecomeAnAffiliate.SummaryText")}
                  </p>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={isAffiliateDisabled}
                  onClick={handleAffiliatePopupModal}
                  sx={{
                    mt: 2,
                    px: 4,
                    py: 1.5,
                    borderRadius: "10px",
                    fontSize: "1rem",
                  }}
                >
                  {isAlreadyAffiliate
                    ? t("BecomeAnAffiliate.AlreadyAnAffiliate")
                    : t("BecomeAnAffiliate.BecomeConsultant")}
                </Button>
              </Stack>
            </Stack>
          </Card>
        </Box>
      </Box>

      <Dialog
        open={isPopupOpen}
        onClose={handleClosePopup}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            color: "secondary.light",
            borderBottom: "1px solid var(--headerbg)",
            px: 3,
            py: 2,
          }}
        >
          {isAlreadyAffiliate
            ? t("BecomeAnAffiliate.AlreadyAnAffiliate")
            : t("BecomeAnAffiliate.BecomeConsultant")}
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2 }}>
          {mediaSrc && (
            <Box sx={{ width: "100%", mt: 2, mb: 3 }}>
              <Box
                className="embed-responsive embed-responsive-16by9"
                sx={{
                  position: "relative",
                  paddingBottom: "56.25%",
                  height: 0,
                  overflow: "hidden",
                  mb: 2,
                }}
              >
                <iframe
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                  src={mediaSrc}
                  title="Affiliate Information"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </Box>
            </Box>
          )}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" color="secondary.light" sx={{ mb: 1 }}>
              {t("BecomeAnAffiliate.AffiliateInfo")}
            </Typography>

            <Box component="ul" sx={{ pl: 2 }}>
              <Box component="li" sx={{ mb: 1 }}>
                {t("BecomeAnAffiliate.PopUpText1")}
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                {t("BecomeAnAffiliate.PopUpText2")}
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                {t("BecomeAnAffiliate.PopUpText3")}
              </Box>
            </Box>
          </Box>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {termsAgreeCheckbox()}
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  mt: 2,
                }}
              >
                <Button onClick={handleClosePopup} color="inherit">
                  {t("Common.Cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={!watch("agreeToTerms") || isAffiliateDisabled}
                >
                  {t("Common.Confirm")}
                </Button>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
      </Dialog>
      <CommonSnackBar snackObj={snack} closeSnack={closeSnack} buttonProp />
    </>
  );
};

export default BecomeAnAffiliate;